
"use strict";

const webChatConfig = require('./webChatConfig');
const webChatCommonApiUtils = require('./webChatCommonApiUtils');
const $ = require('jquery');

// Variables used for Agent Typing indicator.
let typingIndicatorTimeout = null;
const MILLI_SECONDS_TO_REFRESH_TYPING_ICON = 3000;

/**
 * This function will be responsible for showing the Web Chat Client in a particular state. See Global STATES at top of this file.
 */
const _applyWindowState = (webChatState, extLink) => {
  // If state should be closed then apply this first, otherwise apply other states. The closed state
  // needs to be applied differently if agents are off-line or user in queue.
  if (webChatState === webChatConfig.STATE_CLOSED())  {
    // Handle the state passed in.
    processWindowState(webChatState, extLink);

  } else if (webChatState === webChatConfig.STATE_OPEN_MINIMISED()) {
    webChatConfig.setWebChatWindowMinimised(true);
    webChatConfig.setChatWindowOpen(false);
    // Handle the state passed in.
    processWindowState(webChatState, extLink);

  } else {
    // Set flag to indicate Web Chat Window is maximised
    webChatConfig.setChatWindowOpen(true);
    webChatConfig.setWebChatWindowMinimised(false);
    // Handle the state passed in.
    processWindowState(webChatState, extLink);
  }
};

/**
 * Handle the state passed in by applying logic to the Web Chat Client Window.
 */
const processWindowState = (state, externalLink) => {
  let windowState = {
    0: () => {     // webChatConfig.STATE_CLOSED()
        applyStateClosed();
    },
    1: () => {     // webChatConfig.STATE_OPEN_ENTER_NAME()
        applyStateOpenEnterName();
    },
    2: () => {     // webChatConfig.STATE_OPEN_AGENTS_OFFLINE()
        applyStateOpenAgentsOffline();
    },
    3: () => {     // webChatConfig.STATE_OPEN_QUEUE()
        applyStateOpenQueue();
    },
    4: () => {     // webChatConfig.STATE_OPEN_ACTIVE_WEB_CHAT()
        applyStateOpenActiveWebChat();
    },
    5: () => {     // webChatConfig.STATE_OPEN_TIMEOUT_WARNING()
        console.log("About to set Web Chat Client as open showing initial timeout warning message");
        showClientOpenTimeoutWarning();
    },
    6: () => {     // webChatConfig.STATE_OPEN_TIMED_OUT()
        applyStateOpenTimedOut();
    },
    7: () => {     // webChatConfig.STATE_OPEN_END_CONFIRM()
        applyStateOpenEndConfirm();
    },
    8: () => {     // webChatConfig.STATE_OPEN_ENDED()
        applyStateOpenChatEnded();
    },
    9: () => {     // webChatConfig.STATE_OPEN_TRANSCRIPT_EMAIL()
        console.log("About to set Web Chat Client as open input box for email for transcript to be sent to");
        showClientOpenEmailBox();
    },
    10: () => {    // webChatConfig.STATE_OPEN_ENDED_TRANSCRIPT_SENT()
        applyStateOpenTranscriptSent();
    },
    11: () => {    // webChatConfig.STATE_CLOSED_BUBBLE_ONLY()
        applyStateBubbleOnly();
    },
    12: () => {    // webChatConfig.STATE_LEAVING_PORTAL()
        console.log("About to set Web Chat Client as open showing text confirming chat will end if user navigates away from page");
        showClientOpenLeavingPortalText(externalLink);
    },
    13: () => {    // webChatConfig.STATE_OPEN_FEEDBACK
        applyStateFeedBack();
    },
    14: () => {    // webChatConfig.STATE_OPEN_PIN_REQUEST
        applyStatePin2FA();
    },
    15: () => {    // webChatConfig.STATE_CLOSE_PIN_REQUEST
        applyStateHidePin2FA();
    },
    16: () => {   // webChatConfig.STATE_OPEN_MINIMISED
      showClientMinimised();
    }
  };

  // If State is valid and a function exists for it above, execute it, otherwise execute the default logic.
  if (windowState[state]) {
    windowState[state]();
  } else {
    // Default logic so everything is minismised.
    console.log("About to set Web Chat Client as closed (Default)");
    showClientMinimised();
    // Set flag to indicate Web Chat Window is minimised
    webChatConfig.setChatWindowOpen(false);
    webChatConfig.setWebChatWindowMinimised(true);
    _clearAllEventIntervals();
  }
};

/**
 * Set Web Chat Client as minimised (Closed).
 */
const applyStateClosed = () => {
  // User has not clicked on Web Chat so everything is minimised.
  console.log("About to set Web Chat Client as closed");
  showClientMinimised();

  // Set flag to indicate Web Chat Window is minimised
  webChatConfig.setChatWindowOpen(false);
  webChatConfig.setWebChatWindowMinimised(true);
};

/**
 * Window open but waiting on user to enter name.
 */
const applyStateOpenEnterName = () => {
  console.log(
      "About to set Web Chat Client as open waiting for user to enter name"
  );
  showClientOpenEnterName();
};

/**
 * Window open but all agents have gone offline
 */
const applyStateOpenAgentsOffline = () => {
  console.log(
      "About to set Web Chat Client as open with agents offline message showing"
  );
  showClientOpenOffline();
  // Clear out Interval Id from _events() to stop polling for Responses as Web Chat is ended.
  _clearAllEventIntervals();
};

/**
 * Window open but user waiting in queue
 */
const applyStateOpenQueue = () => {
  console.log(
      "About to set Web Chat Client as open with queue messsage showing"
  );
  showClientQueue();
};

/**
 * Window open and user in an active chat
 */
const applyStateOpenActiveWebChat = () => {
  console.log(
      "About to set Web Chat Client as open with active web chat in progress"
  );
  showClientOpenActiveChat();
  // Check in case there are unseen messages and these are inactivity messages
  if (webChatConfig.warningDisplayFlag() === 1) {
    webChatConfig.setWarningDisplayFlag(0);
    console.log(
        "Changing to set Web Chat Client as open showing initial timeout warning message"
    );
    showClientOpenTimeoutWarning();
  } else if (webChatConfig.warningDisplayFlag() === 2) {
    webChatConfig.setWarningDisplayFlag(0);
    console.log(
        "Changing to set Web Chat Client as open showing timed out message"
    );
    showClientOpenTimedOut();
    // Clear out Interval Id from _events() to stop polling for Responses as Web Chat is ended.
    _clearAllEventIntervals();
  }
};

/**
 * Window open and showing Timed Out message in Web Chat client window.
 */
const applyStateOpenTimedOut = () => {
  console.log("About to set Web Chat Client as open showing timed out message");
  showClientOpenTimedOut();
  // Clear out Interval Id from _events() to stop polling for Responses as Web Chat is ended.
  _clearAllEventIntervals();
};

/**
 * Window open and showing end confirm questions to user.
 */
const applyStateOpenEndConfirm = () => {
  console.log(
      "About to set Web Chat Client as open showing confirm questions ending chat"
  );
  // Only show end chat confirmation question if a Chat was active.
  if (webChatConfig.chatActive()) {
    showClientOpenConfirmEndChat();
  } else {
    showBubbleOnly();
  }
};

/**
 * Showing chat ended text in Web Chat client window.
 */
const applyStateOpenChatEnded = () => {
  console.log(
      "About to set Web Chat Client as open chat showing ended text without sending transcript message"
  );
  showClientOpenEndedChat();
  // Clear out Interval Id from _events() to stop polling for Responses as Web Chat is ended.
  _clearAllEventIntervals();
};

/**
 * Showing chat ended and transcript sent message text in Web Chat client window.
 */
const applyStateOpenTranscriptSent = () => {
  console.log(
      "About to set Web Chat Client as open chat showing ended text after sending transcript message"
  );
  showTranscriptSent();
  // Clear out Interval Id from _events() to stop polling for Responses as Web Chat is ended.
  _clearAllEventIntervals();
};

/**
 * Showing chat ended text in Web Chat client window.
 */
const applyStateFeedBack = () => {
  webChatConfig.setFeedBackActive(true);
  console.log(
      "About to set Web Chat Client as open chat showing the question for feedback"
  );
  showClientOpenFeedBackQuestion();
};

/**
 * Showing PIN Form for 2FA
 */
const applyStatePin2FA = () => {
  console.log("About to set Web Chat Client as open PIN input form for 2FA");
  webChatConfig.set2FAActive(true);
  showPinFormFor2FA();
};

const applyStateHidePin2FA = () => {
  webChatConfig.set2FAActive(false);
  hidePinFormFor2FA();
};

/**
 * Showing Web Chat client as closed and bubble icon only showing.
 */
const applyStateBubbleOnly = () => {
  console.log(
      "About to set Web Chat Client as closed and hidden and only bubble showing"
  );
  showBubbleOnly();
  // Clear out Interval Id from _events() to stop polling for Responses as Web Chat is ended.
  _clearAllEventIntervals();
};

/**
 * Show Web Chat Client in the minimised position.
 */
const showClientMinimised = () => {
  // Show Web Chat Client Window
  showElement("#webChatClient");
  // Hide The Message display window
  closeMessageDisplay();
  // Hide the Down Arrow and show the Up Arrow
  hideElement("#iconBarMinimise");
  hideElement("#iconBarX");
  $("#webChatClient").addClass("wc-client--minimised");
  $("#webChatIconBar").addClass("wc-client__iconbar--minimised");
  $("#iconBarBubble").addClass("wc-client__iconbar-bubble--minimised");

  // Remove Border from message input div when minimised
  hideEnterMessageBar();
  // Show notifications icon if chat is active
  if (webChatConfig.chatActive()) {
    _updateNotificationIcon();
  }

};

/**
 * Show Web Chat Client Window in open state with an active Web Chat.
 */
const showClientOpenActiveChat = () => {
  // Ensure Web Chat Client is open
  showElement("#webChatClient");

  // Ensure the number of unread messages is set to zero and the notification icon is hidden.
  webChatConfig.setUnreadMessagesCount(0);

  // Hide all window elements for Web Chat Client.
  hideWindowElements();
  // Hide unread notification icon
  hideElement("#notificationIcon");

  // Open the Message display and ensure existing messages are shown
  openMessageDisplay();
  $("#messageDisplay").addClass("messageDisplayActiveSmall");
  showElement(".messageDiv");
  showElement("#conversation");
  showElement("#iconBarX");
  // Show the enter message input box.
  showEnterMessageBar();
  // Scroll to bottom of message window
  scrollBottom("#conversation");

  // Show agent name in message bar heading text.
  if (webChatConfig.agentName() !== null && webChatConfig.agentName() !== "") {
    $("#connectedAgentName").text(webChatConfig.agentName());
  } else {
    $("#connectedAgentName").text("Agent");
  }
};

/**
 *
 */
const showClientOpenEnterName = () => {
  // Show the Web Chat Message Display Window
  openMessageDisplay();

  // Clear any left over messages in case there was a previous chat.
  _clearExistingConversation();

  // Ensure message window goes to bottom of screen.
  $("#messageDisplay").addClass("adjustHeight");

  // Hide all window elements for Web Chat Client.
  hideWindowElements();
  // Hide unread notification icon
  hideElement("#notificationIcon");

  $("#webChatClientWrapper").removeClass("wc-client-wrapper--minimised");
  showElement("#iconBarX");
  showElement("#conversation");
  // Hide Enter Message Bar and Show Name Form
  hideEnterMessageBar();
  showElement("#enterName");
  // Set focus on the enter name field.
  let webChatFirstName = $("#webChatFirstName");
  webChatFirstName.focus();
  webChatFirstName.attr("maxlength", 30);
};

/**
 *
 */
const showClientOpenOffline = () => {
  // Hide all window elements for Web Chat Client.
  hideWindowElements();

  // Show content text
  showElement("#agentsOfflineText");
  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  hideElement("#conversation");
  // Open Client display window.
  openMessageDisplay();
};

/**
 *
 */
const showClientQueue = () => {
  // Hide all window elements for Web Chat Client.
  hideWindowElements();
  // Ensure Web Chat Client is open.
  openMessageDisplay();

  // Show content text
  showElement("#queueText");
  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  hideElement("#conversation");
};

/**
 * This will show the Web Chat Client window open with text to indicate the chat will end
 * and buttons for the user to cancel, confirm, or request a transcript to be sent.
 */
const showClientOpenConfirmEndChat = () => {
  // Hide all window elements for Web Chat Client.
  hideWindowElements();
  // Hide unread notification icon
  hideElement("#notificationIcon");

  // Ensure Web Chat Client is open.
  openMessageDisplay();

  // Show confirm end content text
  showElement("#requestTranscriptContent");
  showElement("#confirmEndText");

  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  showSendTranscriptButton();
  hideElement("#conversation");
  moveMessageWindowToScreenBottom();
};

/**
 * This will show the Web Chat Client window open with text to indicate that the user
 * is navigating away from VHI.IE and that  the chat will end and buttons for the user
 * to cancel, confirm, or request a transcript to be sent.
 */
const showClientOpenLeavingPortalText = (extLink) => {
  // Hide all window elements for Web Chat Client.
  hideWindowElements();

  // Ensure Web Chat Client is open.
  openMessageDisplay();

  // Show navigating away from portal confirm end content text
  showElement("#requestTranscriptContent");
  // Show text depending on Link clicked on.
  if (extLink !== null && extLink === webChatConfig.EXT_LINKS().MYVHI) {
    showElement("#navigatingMyVhiAwayText");
  } else if (
      extLink !== null &&
      extLink === webChatConfig.EXT_LINKS().MYVHILOGOUT
  ) {
    showElement("#navigatingMyVhiLogoutAwayText");
  } else if (extLink !== null && extLink === webChatConfig.EXT_LINKS().AEM) {
    showElement("#navigatingAemAwayText");
  }

  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  showSendTranscriptButton();
  hideElement("#conversation");
  moveMessageWindowToScreenBottom();
};

/**
 *
 */
const showClientOpenTimeoutWarning = () => {
  // Hide all window elements for Web Chat Client.
  hideWindowElements();

  // Show confirm end content text
  showElement("#requestTranscriptContent");
  showElement("#confirmEndTimeoutText");
  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  showSendTranscriptButton();
  hideElement("#conversation");
  moveMessageWindowToScreenBottom();
};

/**
 * This will hide the send transcript button and only show it if the user has sent at least
 * one message to the agent.
 */
const showSendTranscriptButton = () => {
  hideElement("#endChatConfirmTranscriptCTA");
  $(".messageDiv").each(function (i, obj) {
    if (
        $(obj).find(".userMessage").length > 0 &&
        webChatConfig.isAuthenticated()
    ) {
      // show send transcript button
      showElement("#endChatConfirmTranscriptCTA");
    }
  });
};

/**
 *
 */
const showClientOpenEmailBox = () => {
  // Hide all window elements for Web Chat Client.
  hideWindowElements();

  // Show the transcript Email address
  showElement("#emailAddressContent");
  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  hideElement("#conversation");
  moveMessageWindowToScreenBottom();
};

/**
 * Show client feedBack
 */
const showClientOpenFeedBackQuestion = () => {
  // Hide elements on window
  hideWindowElements();
  // Show confirm end content text
  showElement("#webChatFeedBackQuestionContent");

  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  hideElement("#conversation");
  moveMessageWindowToScreenBottom();
  // Clear out field values and reset flags as chat is no longer active.
  resetFields();
  _resetAllFlags();
};

const showPinFormFor2FA = () => {
  // Hide elements on window
  hideWindowElements();

  openMessageDisplay();
  showElement("#TwofaPinInputContent");

  hideEnterMessageBar();
  hideElement("#conversation");
  moveMessageWindowToScreenBottom();
};

const hidePinFormFor2FA = () => {
  showEnterMessageBar();
  showElement("#conversation");

  hideElement("#TwofaPinInputContent");

  let messageDisplay = $("#messageDisplay");
  messageDisplay.addClass("messageDisplayActiveSmall");
  messageDisplay.addClass("messagePadding");
  messageDisplay.addClass("adjustHeight");
};

/**
 *
 */
const showClientOpenEndedChat = () => {
  // Hide elements on window
  hideWindowElements();
  // Show confirm end content text
  showElement("#chatEndedCloseContent");
  showElement("#chatEndedText");

  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  hideElement("#conversation");
  moveMessageWindowToScreenBottom();
  // Clear out field values and reset flags as chat is no longer active.
  resetFields();
  _resetAllFlags();
};

/**
 *
 */
const showClientOpenTimedOut = () => {
  // Hide elements on window
  hideWindowElements();
  // Show confirm end content text
  showElement("#chatEndedCloseContent");
  showElement("#chatEndedTimedOutText");

  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  hideElement("#conversation");
  moveMessageWindowToScreenBottom();
  // Clear out field values and reset flags as chat is no longer active.
  resetFields();
  _resetAllFlags();
};

/**
 * Position message window so it is aligned with bottom of screen
 */
const moveMessageWindowToScreenBottom = () => {
  // Ensure message window goes to bottom of screen.
  let messageDisplay = $("#messageDisplay");
  messageDisplay.removeClass("messageDisplayActiveSmall");
  messageDisplay.removeClass("messagePadding");
  messageDisplay.addClass("adjustHeight");
};

/**
 * Show message to indicate that transcript has been sent.
 */
const showTranscriptSent = () => {
  // Hide elements on window
  hideWindowElements();

  // Hide enter message input box and ensure existing messages are hidden if any.
  hideEnterMessageBar();
  hideElement("#conversation");
  // Show the Ended text and close button
  showElement("#chatEndedTranscriptSentCloseContent");
  // Ensure message window goes to bottom of screen.
  let messageDisplay = $("#messageDisplay");
  messageDisplay.removeClass("messagePadding");
  messageDisplay.addClass("adjustHeight");
  // Clear out fields and reset flags as chat is no longer active.
  resetFields();
  _resetAllFlags();
};

/**
 * Hide all window elements. This function can be called from all other functions before showing
 * a particular element on screen to ensure elements will not be shown that are not needed.
 */
const hideWindowElements = () => {
  // Hide Enter Name Form
  hideElement("#enterName");
  // Hide all messages from Message display window.
  hideElement(".messageDiv");
  // Hide the Advisors Offline and Queue text in case they were previously shown.
  hideElement("#agentsOfflineText");
  hideElement("#queueText");
  // Hide confirm end content text
  hideElement("#requestTranscriptContent");
  // Hide feedback windows
  hideElement("#webChatFeedBackQuestionContent");
  hideElement("#webchatFeedBackContent");

  hideElement("#confirmEndText");
  hideElement("#confirmEndTimeoutText");
  // Hide the Navigating away from Portal confirmation text.
  hideElement("#navigatingMyVhiAwayText");
  hideElement("#navigatingAemAwayText");
  // Hide the transcript email address content text
  hideElement("#emailAddressContent");
  // Hide other elements
  hideElement("#chatEndedCloseContent");
  hideElement("#chatEndedText");
  hideElement("#chatEndedTimedOutText");
  hideElement("#chatEndedTranscriptSentCloseContent");
  hideElement("#userTypingMessage");
  hideElement("#TwofaPinInputContent");
};

/**
 *
 */
const showBubbleOnly = () => {
  hideElement("#webChatClient");
  $("#webChatClientWrapper").addClass("wc-client-wrapper--minimised");
  showElement("#bubbleOnly");
  webChatConfig.setChatWindowOpen(false);
  webChatConfig.setWebChatWindowMinimised(true);
  _clearExistingConversation();
};

/**
 *
 */
const _resetAllFlags = () => {
  // Set flag to indicate chat is no longer active.
  webChatConfig.setChatActive(false);
  _clearExistingConversation();
  // Call Server side controller to clear out web chat from session. No messages will exist in client window
  // so a blank object will be stored in session.
  _clearWebChatFromSession();
};

/**
 * Clear out existing conversation from web chat client window.
 */
const _clearExistingConversation = () => {
  // Remove all existing messages from chat window
  $("#conversation").children(".messageDiv").remove();
  // Hide the Agent introduction message and remove the time, agent name and user name.
  $("#agentIntroductionAgentName").text("");
  $("#agentIntroductionUserName").text("");
  $("#agentIntroductionTime").text("");
  hideElement("#agentIntroduction");
  if (!webChatConfig.isAuthenticated()) {
    // change authenticated 'Start Chat' text button back to default purple arrow up button
    $('#startChatCTAAuthText').hide();
    $('#startChatCTA').removeClass('myvhi-start-chat-button-text ');
  }
};

/**
 *
 */
const _clearWebChatFromSession = () => {
  console.log("CLEARING WEBLOGIC SESSION");

  // Reset required flags
  webChatConfig.setChatActive(false);
  webChatConfig.setNbrConnectedAgents(null);

  // TODO - Should be able to combine this with storeActiveWebChatInSession() from webChat.js

  // Set blank messages in session
  let jsonMessages = [];

  let webChatContent = {
    // Build up an array of messages
    messages: jsonMessages,
    userName: null,
    agentName: null,
    webChatHistoryStored: false,
    urlRepository: null,
    chatActive: false,
    nbrConnectedAgents: null,
    webChatWindowMinimised: false,
    unreadMessagesCount: null,
  };

  webChatCommonApiUtils.storePortalSession(webChatContent);

  console.log("FINISHED CLEARING WEBLOGIC SESSION");
};

/**
 * Show Enter Message bar and add Border to Div.
 */
const showEnterMessageBar = () => {
  // Hide all window elements for Web Chat Client.
  hideWindowElements();
  showElement("#enterMessageBar");
  // Make sure all old messages are visible and End Chat content is hidden.
  showElement(".messageDiv");
  // Ensure CSS is applied correctly to Message display area
  $("#messageDisplay").addClass("messagePadding");
  $("#messageDisplay").removeClass("wc-client__message-display--rounded-bottom-left");
};

/**
 * Hide the Enter Message Bar and remove border.
 */
const hideEnterMessageBar = () => {
  $("#enterMessageBar").css({ "border-style": "", "border-width": "" });
  hideElement("#enterMessageBar");
  $("#messageDisplay").addClass("wc-client__message-display--rounded-bottom-left");
};

/**
 * This function will open the message display section of the Web Chat Client.
 */
const openMessageDisplay = () => {
  showElement("#messageDisplay");
  moveMessageWindowToScreenBottom();
  let webChatIconBar = $("#webChatIconBar");
  let webChatClient = $("#webChatClient");
  webChatIconBar.removeClass("bottom");
  webChatIconBar.addClass("iconBarHeight");
  showElement("#iconBarMinimise");
  webChatClient.removeClass("wc-client--minimised");
  webChatIconBar.removeClass("wc-client__iconbar--minimised");
  $("#iconBarBubble").removeClass("wc-client__iconbar-bubble--minimised");
};

/**
 * This function will close the message display section of the Web Chat Client
 */
const closeMessageDisplay = () => {
  hideElement("#messageDisplay");
  let webChatIconBar = $("#webChatIconBar");
  webChatIconBar.removeClass("messageDisplayHeight");
  webChatIconBar.addClass("bottom");
};

/**
 * This function will scroll to the bottom of the element passed in.
 */
const scrollBottom = (element) => $(element).scrollTop($(element)[0].scrollHeight);

/**
 * This will write messages from user and agent to web chat client window.
 */
const _writeMessageInDisplay = (name, text, timeClass, messageFrom) => {
  // Hide the Agent is typing message in case it's currently showing.
  hideElement("#userTypingMessage");

  // If display name matches user name or guest or matches Anonymous, then this is a user message.
  if (
      name !== null && messageFrom === "USER" &&
      (name === webChatConfig.userName() || name === "Anonymous" || name === "guest")
  ) {
    $("<div class='messageDiv'><div class='userMessage content18'>" + text + "</div>" +
        "<div class=\"" + timeClass + "\">" + _getCurrentTime() + "</div>")
        .insertBefore("#userTypingMessage");
  }
  // Otherwise show it as an agent message
  else {
    $("<div class='messageDiv'><div class='agentMessage content25'>" + text + "</div>" +
        "<div class=\"" + timeClass + "\">" + _getCurrentTime() + "</div></div>")
        .insertBefore("#userTypingMessage");
  }

  scrollBottom("#conversation");
};

/**
 * This will write agent transfer messages to web chat client windown.
 */
const _writeTextMessageInDisplay = (text) => {
  $("<div class='messageDiv'>" + text + "</div>").insertBefore(
      "#userTypingMessage"
  );
  scrollBottom("#conversation");
};

/**
 * Show agent typing indicator for a set period of time.
 */
const _writeUserTypingMessageInDisplay = (name) => {
  // Show agent typing message.
  showAgentUserTypingMessage(name);

  clearTypingIndicatorTimeout();
  typingIndicatorTimeout = setTimeout(
      hideAgentUserTypingMessage,
      MILLI_SECONDS_TO_REFRESH_TYPING_ICON
  );
};

/**
 * Show agent typing indicator on screen.
 */
const showAgentUserTypingMessage = (name) => {
  // Append Agent name to the Typing text.
  $("#userTypingAgentName").text(name);
  // Show the Agent is typing message.
  showElement("#userTypingMessage");
  scrollBottom("#conversation");
};

/**
 * Clear typing indicator timeout value.
 */
const clearTypingIndicatorTimeout = () => {
  if (typingIndicatorTimeout !== null) {
    clearTimeout(typingIndicatorTimeout);
    typingIndicatorTimeout = null;
  }
};

/**
 * Hide agent typing indicator on screen.
 */
const hideAgentUserTypingMessage = () => {
  // Hide the Agent is typing message.
  hideElement("#userTypingMessage");
  // Clear Agent name from Typing text.
  $("#userTypingAgentName").text("");
};

/**
 * Display the number of unread messages in the notification icon.
 */
const _updateNotificationIcon = () => {
  $("#notificationIcon").text(webChatConfig.unreadMessagesCount());
  showElement("#notificationIcon");
};

/**
 * Show Agent Introduction message in Client Display.
 */
const _setupAgentIntroduction = () => {
  console.log("Agent Introduction");
  $("#agentIntroductionAgentName").text(webChatConfig.agentName());
  $("#agentIntroductionUserName").text(webChatConfig.userName());
  // Add current time to the agent introduction text
  $("#agentIntroductionTime").text(_getCurrentTime());
  // Show the Agent Welcome message.
  showElement("#agentIntroduction");
  _applyWindowState(webChatConfig.STATE_OPEN_ACTIVE_WEB_CHAT());
};

/**
 * Clear all Interval Ids that may have been created when polling for events.
 */
const _clearAllEventIntervals = () => {
  let intervalIds = webChatConfig.eventIntervalIds();
  if (intervalIds !== null && intervalIds.length > 0) {
    console.log(Date() + "Starting to clear all Intervals");
    for (const element of intervalIds) {
      console.log("Clearing Interval : " + element);
      clearInterval(element);
    }
    // Reset array.
    webChatConfig.setEventIntervalIds([]);
    console.log(Date() + "Finished clearing all Intervals");
  }
  // Clear out queue timeout ID just in case.
  _clearQueueTimeout();
  // Also reset queue message shown flag so if user starts a new chat after ending an old one,
  // the queue message will be shown again
  webChatConfig.setQueueMessageAlreadyShown(false);
};

/**
 * Clear the queue timeout to stop polling queue.
 */
const _clearQueueTimeout = () => {
  webChatConfig.setResetQueueMessageFlag(false);
  clearTimeout(webChatConfig.queueEventTimeout);
};

/**
 * Get the current system time.
 */
const _getCurrentTime = () => {
  let currentdate = new Date();
  // Check in case minutes is less than 10, then add a zero in front of it.
  let minutes = currentdate.getMinutes();
  if (minutes !== null && minutes < 10) {
    minutes = "0" + minutes;
  }
  return currentdate.getHours() + ":" + minutes;
};

const resetFields = () => {
  // Clear out the Name, transcript email address and Message input box
  $("#webChatFirstName").val("");
  $("#transcriptEmailAddress").val("");
  $("#message").val("");
};

const showElement = (elementId) => {
  $(elementId).show();
};

const hideElement = (elementId) => {
  $(elementId).hide();
};

function getPageIdFromIframeUrl() {
  let URLSearchParams = window.URLSearchParams;
  let urlSearchParams = new URLSearchParams(window.location.search);
  return urlSearchParams.get('pageid');  
}

module.exports = {
  applyWindowState: _applyWindowState,
  writeMessageInDisplay: _writeMessageInDisplay,
  writeUserTypingMessageInDisplay: _writeUserTypingMessageInDisplay,
  writeTextMessageInDisplay: _writeTextMessageInDisplay,
  clearWebChatFromSession: _clearWebChatFromSession,
  clearAllEventIntervals: _clearAllEventIntervals,
  clearExistingConversation: _clearExistingConversation,
  clearQueueTimeout: _clearQueueTimeout,
  setupAgentIntroduction: _setupAgentIntroduction,
  updateNotificationIcon: _updateNotificationIcon,
  getCurrentTime : _getCurrentTime,
  resetFields: resetFields,
  showElement: showElement,
  hideElement: hideElement,
  getPageIdFromIframeUrl: getPageIdFromIframeUrl
};